import StyledAuthContainer from '@/components/auth/StyledAuthContainer';
import { PATHS } from '@/constants/Paths';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/i18n';
import { AuthProfile } from '@/types/auth/AuthProfile';
import { AuthStatus } from '@/types/auth/AuthStatus';
import { getDashboardPath, getProfileLabel } from '@/utils/authProfile';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

type ProfileSelectorPageProps = {
  profiles: AuthProfile[];
  selectionToken?: string;
  mode: 'login' | 'switch';
};

const ProfileSelectorPage: React.FC<ProfileSelectorPageProps> = ({
  profiles: profilesProp,
  selectionToken: selectionTokenProp,
  mode,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    selectProfile,
    switchProfile,
    pendingProfiles,
    pendingSelectionToken,
  } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const profiles = profilesProp.length > 0 ? profilesProp : pendingProfiles;
  const selectionToken = selectionTokenProp ?? pendingSelectionToken;

  useEffect(() => {
    if (profiles.length <= 1 && mode === 'login') {
      navigate(PATHS.auth.login);
    }
  }, [profiles.length, mode, navigate]);

  const handleSelect = async (userId: string) => {
    setErrorMessage(undefined);
    setIsLoading(true);
    try {
      const result =
        mode === 'switch'
          ? await switchProfile(userId)
          : await selectProfile(userId, selectionToken ?? '');
      if (result.status !== AuthStatus.OK || !result.userType) {
        setErrorMessage(t('auth.invalidCredentials'));
        return;
      }
      navigate(getDashboardPath(result.userType), { replace: true });
      window.location.reload();
    } catch {
      setErrorMessage(t('auth.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledAuthContainer
      title={t('auth.profileSelector.title')}
      footerMessage=""
      footerLink={<span />}
    >
      <p className="text-sm text-gray-600 mb-6">
        {t('auth.profileSelector.description')}
      </p>
      <div className="flex flex-col gap-3 w-full">
        {profiles.map((profile) => (
          <button
            key={profile.userId}
            type="button"
            disabled={isLoading}
            onClick={() => handleSelect(profile.userId)}
            className="flex items-center gap-4 w-full p-4 rounded-lg border border-gray-200 hover:border-brand-500 hover:bg-brand-50 transition-colors text-left disabled:opacity-50"
          >
            {profile.organizationLogoUrl && (
              <img
                src={profile.organizationLogoUrl}
                alt=""
                className="h-10 w-10 object-contain rounded"
              />
            )}
            <div className="flex flex-col gap-0.5">
              <span className="font-medium text-gray-900">
                {getProfileLabel(profile, t)}
              </span>
              <span className="text-sm text-gray-600">
                {profile.organizationName ?? t('auth.profileSelector.unnamed')}
              </span>
            </div>
          </button>
        ))}
      </div>
      {errorMessage && (
        <p className="text-xs text-error-500 mt-4">{errorMessage}</p>
      )}
    </StyledAuthContainer>
  );
};

export default ProfileSelectorPage;
